/**
 * QuickBooks re-sync helpers shared by the dashboard panel, the start pop-up and
 * the review page. Months travel as 'YYYY-MM'; statuses / reason codes come from
 * gl-api (src/apps/qb_sync/service.py and qbd.py).
 */
export type SyncSource = 'qbo' | 'qbd';
export type SessionStatus = 'collecting' | 'ready' | 'applying' | 'applied' | 'failed' | 'cancelled';
export type MonthStatus = 'ready' | 'partial' | 'not_selected' | 'held' | 'waiting' | 'blocked';

export interface SyncMonth {
    month: string;
    new: number;
    edited: number;
    deleted: number;
    status_only: number;
    total: number;
    status: MonthStatus;
    selected: boolean;
    reasons: string[];
    includes_month_import?: boolean;
    decisions: Record<string, number>;
}

export const OPEN_STATUSES: SessionStatus[] = ['collecting', 'ready', 'applying'];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** '2026-07' -> 'Jul 2026' ('undated' / empty stay as-is). */
export const monthLabel = (value?: string | null): string => {
    if (!value) return '';
    const m = /^(\d{4})-(\d{2})/.exec(value);
    return m ? `${MONTHS[Number(m[2]) - 1]} ${m[1]}` : value;
};

/** ISO date/time -> 'Oct 26, 2026, 11:55' in the browser's locale (UTC from the server). */
export const dateTimeLabel = (value?: string | null): string => {
    if (!value) return '';
    const d = new Date(/[zZ]|[+-]\d{2}:\d{2}$/.test(value) ? value : `${value}Z`);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
};

export const sourceName = (source: SyncSource | null | undefined): string =>
    (source === 'qbd' ? 'QuickBooks Desktop' : 'QuickBooks Online');

/** A month row can be ticked / unticked while the session is ready. */
export const isSelectable = (m: SyncMonth): boolean =>
    ['ready', 'partial', 'not_selected'].includes(m.status);

/** Pill colour class per month status (pill classes exist in forecast.css / app css). */
export const STATUS_PILL: Record<MonthStatus, string> = {
    ready: 'bg-primary',
    partial: 'bg-warning text-dark',
    not_selected: 'bg-secondary',
    held: 'bg-warning text-dark',
    waiting: 'bg-light text-dark border',
    blocked: 'bg-light text-dark border',
};

/** Default English text per reason code (i18n key: reason.<code>). */
export const REASON_TEXT: Record<string, string> = {
    after_sync_through: 'After "Sync through" -- picked up by a later re-sync',
    on_or_after_go_live: 'RevRex is the source from this month (live in RevRex)',
    books_closed: 'Books are closed for this period',
    before_beginning_balance: 'Before the opening balance',
    edit_not_supported_yet: 'Edited in QuickBooks -- edits to imported transactions come in a later release',
    delete_not_supported_yet: 'Deleted in QuickBooks -- deletes come in a later release',
    imported_before_links: 'Probably brought in by the first import -- not imported again',
    record_not_received: 'The connector did not send this transaction -- run Sync changes again',
};

/** Connector entity types -> readable names; QBO types are already readable. */
export const ENTITY_TEXT: Record<string, string> = {
    invoices: 'Invoice', bills: 'Bill', payments: 'Payment', sales_receipts: 'Sales Receipt',
    bill_payments: 'Bill Payment', journal_entries: 'Journal Entry', checks: 'Check',
    credit_card_charges: 'Credit Card Charge', credit_card_credits: 'Credit Card Credit',
    deposits: 'Deposit', transfers: 'Transfer', payroll_journal: 'Payroll',
};

export const entityLabel = (value: string): string => ENTITY_TEXT[value] || value;

/** Months that can be applied: ticked ones, oldest first. */
export const selectedMonths = (months: SyncMonth[]): string[] =>
    months.filter((m) => m.selected && isSelectable(m)).map((m) => m.month).sort();

/**
 * New months must be applied in order (each month's balance sheet carries forward):
 * returns the first unticked selectable month that has a ticked month after it.
 * Mirrors gl-api's service.selection_gap; months up to ``importedThroughMonth``
 * (changes to months already imported) don't count.
 */
export const selectionGap = (months: SyncMonth[], importedThroughMonth?: string | null): string | null => {
    let unticked: string | null = null;
    for (const m of [...months].sort((a, b) => a.month.localeCompare(b.month))) {
        if (!isSelectable(m) || (importedThroughMonth && m.month <= importedThroughMonth)) continue;
        if (!m.selected && unticked === null) unticked = m.month;
        else if (m.selected && unticked !== null) return unticked;
    }
    return null;
};

/** Default English labels (i18n keys monthStatus.*, change.*, decision.*, applyStatus.*). */
export const MONTH_STATUS_TEXT: Record<MonthStatus, string> = {
    ready: 'Ready', partial: 'Partly ready', not_selected: 'Not selected',
    held: 'Needs attention', waiting: 'Waiting', blocked: 'Not imported',
};
export const CHANGE_TEXT: Record<string, string> = {
    new: 'New', edited: 'Edited', deleted: 'Deleted', status_only: 'Status only',
};
export const DECISION_TEXT: Record<string, string> = {
    apply: 'Will be imported', skip: 'Not selected', held: 'Needs attention', blocked: 'Not imported',
};
export const APPLY_STATUS_TEXT: Record<string, string> = {
    applied: 'Imported', failed: 'Failed', skipped: 'Already imported',
};
